import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { FieldValue } from 'firebase/firestore';
import type { User } from '@/lib/types';
import { AVATAR_COLOR_PALETTE_HEX, normalizeAvatarColorHex } from '@/lib/colors';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Get the color for a user, either from their custom color or a default
 * @param user - User object or undefined
 * @param fallbackColor - Optional fallback color (defaults to avatar slate)
 */
export function getUserColor(user: User | undefined, fallbackColor?: string): string {
  const fallbackHex = fallbackColor || AVATAR_COLOR_PALETTE_HEX[AVATAR_COLOR_PALETTE_HEX.length - 1];
  return normalizeAvatarColorHex(user?.color || fallbackHex);
}

/**
 * Recursively removes undefined values from an object while preserving
 * Firestore FieldValue objects (like serverTimestamp(), arrayUnion(), etc.)
 * 
 * @param obj - Any object or value to clean
 * @returns The cleaned object with undefined values removed and FieldValue objects preserved
 * 
 * Note: Uses 'any' type intentionally to handle dynamic Firestore data structures
 */
export function cleanFirestoreData(obj: any): any {
  if (obj === null || typeof obj !== 'object') return obj;
  // Preserve Firestore FieldValue objects (like serverTimestamp())
  if (obj instanceof FieldValue) return obj;
  if (Array.isArray(obj)) return obj.map(cleanFirestoreData);
  const cleaned: any = {};
  for (const key in obj) {
    if (obj[key] !== undefined) {
      cleaned[key] = cleanFirestoreData(obj[key]);
    }
  }
  return cleaned;
}

/**
 * List of supported currencies with their details
 */
export const CURRENCIES = [
  { code: 'GBP', name: 'British Pound', symbol: '£', locale: 'en-GB' },
  { code: 'USD', name: 'US Dollar', symbol: '$', locale: 'en-US' },
  { code: 'EUR', name: 'Euro', symbol: '€', locale: 'de-DE' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', locale: 'ja-JP' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', locale: 'en-AU' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', locale: 'en-CA' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', locale: 'de-CH' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', locale: 'zh-CN' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹', locale: 'en-IN' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', locale: 'en-SG' },
];

/**
 * Get currency formatter based on currency code
 * @param currencyCode - Currency code (e.g., 'GBP', 'USD', 'EUR')
 * @returns Intl.NumberFormat instance
 */
export function getCurrencyFormatter(currencyCode?: string): Intl.NumberFormat {
  const currency = CURRENCIES.find(c => c.code === currencyCode) || CURRENCIES[0];
  return new Intl.NumberFormat(currency.locale, { style: 'currency', currency: currency.code });
}

/**
 * Get currency symbol from code
 * @param currencyCode - Currency code (e.g., 'GBP', 'USD', 'EUR')
 * @returns Currency symbol
 */
export function getCurrencySymbol(currencyCode?: string): string {
  const currency = CURRENCIES.find(c => c.code === currencyCode) || CURRENCIES[0];
  return currency.symbol;
}

/**
 * Format currency value with negative values displayed in parentheses
 * @param value - The numeric value to format
 * @param formatter - Optional Intl.NumberFormat instance (defaults to GBP)
 * @returns Object with formatted string and isNegative boolean
 */
export function formatCurrency(value: number, formatter?: Intl.NumberFormat): { formatted: string; isNegative: boolean } {
  const defaultFormatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
  const fmt = formatter || defaultFormatter;
  const isNegative = value < 0;
  
  if (isNegative) {
    // Format as positive value and wrap in parentheses
    const positiveFormatted = fmt.format(Math.abs(value));
    return { formatted: `(${positiveFormatted})`, isNegative: true };
  }
  
  return { formatted: fmt.format(value), isNegative: false };
}
